import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { ClockIcon, UsersIcon, ArrowUpIcon, ArrowDownIcon } from './Icons';
import { useAuth } from '../contexts/AuthContext';

const Footer: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [countdown, setCountdown] = useState('00:00:00');
  const [isEtdCritical, setIsEtdCritical] = useState(false);
  const [isReceiving, setIsReceiving] = useState(false);
  const { selectedFlightData, lastReceived, isSending, areNotificationsEnabled, showNotification, serverTimeOffset } = useFlight();
  const { user } = useAuth();
  
  // State for new audio/visual alerts
  const [lastAnnouncedMinute, setLastAnnouncedMinute] = useState<number | null>(null);
  const [isFlashing, setIsFlashing] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
        setCurrentTime(new Date(new Date().getTime() + serverTimeOffset))
    }, 1000);
    return () => clearInterval(timer);
  }, [serverTimeOffset]);

  const speak = useCallback(async (text: string) => {
    if (!areNotificationsEnabled) return;
    console.log(`[TTS Feature Removed] Announcement: ${text}`);
  }, [areNotificationsEnabled]);


  useEffect(() => {
    const etd = selectedFlightData?.counterDistribution.etd;
    if (!etd) {
      setCountdown('--:--:--');
      setIsEtdCritical(false);
      setLastAnnouncedMinute(null);
      return;
    }

    const countdownTimer = setInterval(() => {
      const etdTime = new Date(etd).getTime();
      const now = new Date().getTime() + serverTimeOffset;
      const difference = etdTime - now;

      if (difference <= 0) {
        setCountdown('00:00:00');
        setIsEtdCritical(false);
        if (lastAnnouncedMinute !== 0) {
          setLastAnnouncedMinute(0);
          const message = "Estimated time of departure.";
          speak(message);
          showNotification(message, 'info');
          setIsFlashing(true);
          setTimeout(() => setIsFlashing(false), 1000);
        }
      } else {
        setIsEtdCritical(difference <= 10 * 60 * 1000);

        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        if (seconds === 0 && minutes >= 1 && minutes <= 10) {
          if (minutes !== lastAnnouncedMinute) {
            setLastAnnouncedMinute(minutes);
            const minuteText = minutes === 1 ? 'one minute' : `${minutes} minutes`;
            const message = `${minuteText} to departure.`;
            speak(message);
            showNotification(message, 'info');
            setIsFlashing(true);
            setTimeout(() => setIsFlashing(false), 1000);
          }
        }
        
        if (minutes > 10 && lastAnnouncedMinute !== null) {
          setLastAnnouncedMinute(null);
        }

        setCountdown(
          `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        );
      }
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [selectedFlightData?.counterDistribution.etd, lastAnnouncedMinute, speak, showNotification, serverTimeOffset]);
  
  useEffect(() => {
      if (lastReceived > 0) {
        setIsReceiving(true);
        const timeout = setTimeout(() => setIsReceiving(false), 500);
        return () => clearTimeout(timeout);
      }
  }, [lastReceived]);


  const timeString = currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  return (
    <footer className="flex-shrink-0 z-40 bg-slate-900/70 backdrop-blur-lg border-t border-slate-800 text-xs sm:text-sm no-print">
      <div className="container mx-auto flex justify-between items-center flex-wrap gap-2 px-4 sm:px-6 py-1">
        <div className="flex items-center space-x-2 sm:space-x-4">
           <div className="flex items-center space-x-1">
             <ClockIcon className="w-4 h-4 text-blue-400"/>
             <span className="font-mono text-slate-300">{timeString}</span>
           </div>
           <div>
             <span className="font-bold text-slate-400">ETD Countdown:</span>
             <span className={`font-mono ml-2 text-2xl ${isEtdCritical ? 'text-red-400 animate-intense-pulse' : 'text-amber-400'} ${isFlashing ? 'animate-flash' : ''}`}>{countdown}</span>
           </div>
        </div>
         <div className="w-full sm:w-auto text-center sm:text-left text-slate-500 text-[10px]">
            Crew-movement Developed By John tawfik One Of EAS Team Members v 7I
        </div>
        <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1" title="Data sending to server">
                    <ArrowUpIcon className={`w-4 h-4 transition-colors ${isSending ? 'text-green-500 animate-pulse' : 'text-slate-600'}`} />
                    <span className={`font-mono text-xs transition-colors ${isSending ? 'text-green-400' : 'text-slate-500'}`}>SEND</span>
                </div>
                <div className="flex items-center space-x-1" title="Data received from server">
                    <ArrowDownIcon className={`w-4 h-4 transition-colors ${isReceiving ? 'text-red-500 animate-pulse' : 'text-slate-600'}`} />
                    <span className={`font-mono text-xs transition-colors ${isReceiving ? 'text-red-400' : 'text-slate-500'}`}>RECV</span>
                </div>
            </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;